import {Alert, Button, Col, Row, Tabs} from "antd";
import {CustomerDashboardWrapper} from "../../../../layouts/customer-dashboard-wrapper";
import {StyledPageHeader} from "../../../../styles/dashboard/calls/calls";
import {StyledNoUpcomingCallBox} from "../../../../components/calls/upcoming-calls/upcoming-calls.style";
import {useEffect, useState} from "react";
import {ErrorBoundary} from "react-error-boundary";
import {useNavigate, useSearchParams} from "react-router-dom";
import {CustomerProductListPage} from "../../../../components/products/customer-dashboard/product-list";
import {CustomerSubscriptionListPage} from "../../../../components/subscriptions/customer-dashboard/subscription-list";
import {CustomerServicesEntryPoint} from "./services";
import {GetCustomerStoreServicesOrdersContextProvider} from "../../../../service-feature/useCases/get-orders/context/customer";
import {useGetCustomerProducts} from "../../../../react-query-hooks/products";
import {useGetCustomerStoreServicesOrders} from "../../../../react-query-hooks/store/services/get-orders";
import {useListCustomerPurchasedSubscriptions} from "../../../../react-query-hooks/customers/subscriptions/list-customer-subscriptions";

export function CustomerProductsPage() {
  const pageTitle = "Products";

  const [searchParams] = useSearchParams();
  const activeTab = searchParams.get("activeTab");
  const [defaultActiveKey, setDefaultActiveKey] = useState<
    "digital-products" | "services" | "subscriptions"
  >("services");
  const navigate = useNavigate();

  useEffect(() => {
    if (
      activeTab === "digital-products" ||
      activeTab === "services" ||
      activeTab === "subscriptions"
    ) {
      setDefaultActiveKey(activeTab);
    }
  }, [activeTab]);

  function handleTabClick(key: string) {
    if (key === "digital-products") {
      setDefaultActiveKey("digital-products");
      navigate("/customer/products?activeTab=digital-products");
    }
    if (key === "services") {
      setDefaultActiveKey("services");
      navigate("/customer/products?activeTab=services");
    }
    if (key === "subscriptions") {
      setDefaultActiveKey("subscriptions");
      navigate("/customer/products?activeTab=subscriptions");
    }
  }

  return (
    <CustomerDashboardWrapper pageTitle={pageTitle}>
      <StyledPageHeader>Store</StyledPageHeader>
      <div style={{marginTop: "4.6rem"}}>
        <Row gutter={[36, 30]}>
          <Col span={24} lg={{span: 16}}>
            <CustomerStorePageEntryPoint
              defaultActiveKey={defaultActiveKey}
              handleTabClick={handleTabClick}
            />
          </Col>
          <Col span={24} lg={{span: 8}}></Col>
        </Row>
      </div>
    </CustomerDashboardWrapper>
  );
}

export function CustomerStorePageEntryPoint({
  defaultActiveKey,
  handleTabClick,
}: {
  defaultActiveKey: "digital-products" | "services" | "subscriptions";
  handleTabClick(key: string): void;
}) {
  /** Same defaults as customer orders context (`statusId: -1`, first page). Shares React Query cache with the orders provider. */
  const customerOrdersQuery = useGetCustomerStoreServicesOrders({
    statusId: -1,
    page: 1,
    per_page: 5,
  });
  const customerProductsQuery = useGetCustomerProducts(1);

  const pd = customerProductsQuery.data;
  const products = pd?.data?.products;
  const productsMeta = pd?.meta;
  const productHasAny =
    pd != null && ((products?.length ?? 0) > 0 || (productsMeta?.next_page ?? 0) !== 0);

  const od = customerOrdersQuery.data;
  const orderRows = od?.data?.orders;
  const ordersMeta = od?.meta;
  const ordersHasAny =
    od != null && ((orderRows?.length ?? 0) > 0 || (ordersMeta?.next_page ?? 0) !== 0);

  const subscriptionsPeekEnabled = !productHasAny && !ordersHasAny;

  const customerSubscriptionsPeekQuery = useListCustomerPurchasedSubscriptions(1, 1, {
    enabled: subscriptionsPeekEnabled,
  });

  const subPeek = customerSubscriptionsPeekQuery.data;
  const subscriptionRows = subPeek?.data?.subscriptions;
  const subscriptionsMeta = subPeek?.meta;
  const subscriptionsHasAny =
    subPeek != null &&
    ((subscriptionRows?.length ?? 0) > 0 || (subscriptionsMeta?.next_page ?? 0) !== 0);

  const subscriptionsPeekMaybeLoading =
    subscriptionsPeekEnabled &&
    customerSubscriptionsPeekQuery.isFetching &&
    customerSubscriptionsPeekQuery.data === undefined;

  const awaitingInitialShelfData =
    (customerProductsQuery.isFetching && customerProductsQuery.data === undefined) ||
    (!productHasAny &&
      customerOrdersQuery.isFetching &&
      customerOrdersQuery.data === undefined);

  const hasCustomerStoreTabs =
    productHasAny ||
    ordersHasAny ||
    subscriptionsHasAny ||
    subscriptionsPeekMaybeLoading ||
    awaitingInitialShelfData;

  return (
    <div>
      {hasCustomerStoreTabs ? (
        <Tabs
          defaultActiveKey={defaultActiveKey}
          activeKey={defaultActiveKey}
          onTabClick={(key) => handleTabClick(key)}
        >
          <Tabs.TabPane tab="Services" key="services">
            <ErrorBoundary
              FallbackComponent={({error, resetErrorBoundary}) => (
                <div style={{marginTop: "1.8rem"}}>
                  <Alert
                    message="Sorry, something went wrong."
                    showIcon
                    description={error.message}
                    type="error"
                    action={
                      <Button
                        onClick={() => {
                          resetErrorBoundary();
                        }}
                        size="small"
                        danger
                      >
                        Reset
                      </Button>
                    }
                  />
                </div>
              )}
            >
              <GetCustomerStoreServicesOrdersContextProvider>
                <CustomerServicesEntryPoint />
              </GetCustomerStoreServicesOrdersContextProvider>
            </ErrorBoundary>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Products" key="digital-products">
            <ErrorBoundary
              FallbackComponent={({error, resetErrorBoundary}) => (
                <div style={{marginTop: "1.8rem"}}>
                  <Alert
                    message="Sorry, something went wrong."
                    showIcon
                    description={error.message}
                    type="error"
                    action={
                      <Button
                        onClick={() => {
                          resetErrorBoundary();
                        }}
                        size="small"
                        danger
                      >
                        Reset
                      </Button>
                    }
                  />
                </div>
              )}
            >
              <CustomerProductListPage />
            </ErrorBoundary>
          </Tabs.TabPane>
          <Tabs.TabPane tab="Subscriptions" key="subscriptions">
            <ErrorBoundary
              FallbackComponent={({error, resetErrorBoundary}) => (
                <div style={{marginTop: "1.8rem"}}>
                  <Alert
                    message="Sorry, something went wrong."
                    showIcon
                    description={error.message}
                    type="error"
                    action={
                      <Button
                        onClick={() => {
                          resetErrorBoundary();
                        }}
                        size="small"
                        danger
                      >
                        Reset
                      </Button>
                    }
                  />
                </div>
              )}
            >
              <CustomerSubscriptionListPage />
            </ErrorBoundary>
          </Tabs.TabPane>
        </Tabs>
      ) : (
        <StyledNoUpcomingCallBox>
          <p className="info-text">
            You do not have any products, services, or subscriptions at the moment.
          </p>
        </StyledNoUpcomingCallBox>
      )}
    </div>
  );
}
